import { Navigate, useLocation, useNavigate } from 'react-router-dom'

function formatReward(value) {
  return Number(value || 0).toLocaleString()
}

function formatPercent(value) {
  return `${Number(value || 0)}%`
}

export default function Victory() {
  const navigate = useNavigate()
  const location = useLocation()
  const victory = location.state?.victory || null

  if (!victory) {
    return <Navigate to="/game" replace />
  }

  const history = victory.history || []
  const rewards = victory.rewards || {}
  const rewardBreakdown = rewards.breakdown || {}
  const experience = rewardBreakdown.experience || {}
  const money = rewardBreakdown.money || {}
  const levelPoints = rewardBreakdown.levelPoints || {}
  const clanBonuses = rewardBreakdown.clanBonuses || {}
  const damageSummary = victory.damageSummary || { dealtToEnemy: {}, receivedByBot: {} }
  const finalEnemyState = victory.finalEnemyState || {}
  const loot = victory.loot || rewards.loot || null

  const victoryTitle = victory.enemy?.name || 'Enemigo'

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(34,197,94,0.16),rgba(4,6,13,0.98)_55%)] text-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl rounded-3xl border border-emerald-400/20 bg-[#07111d] shadow-[0_0_70px_rgba(34,197,94,0.14)] overflow-hidden">
        <div className="px-6 py-6 border-b border-emerald-400/20">
          <p className="text-xs uppercase tracking-[0.35em] text-emerald-300/70">Victoria</p>
          <h1 className="text-4xl md:text-5xl font-black text-white mt-2">
            {victoryTitle} derrotado
          </h1>
          <p className="text-slate-300 mt-3">
            Has ganado el combate y el enemigo ha sido eliminado del mapa.
          </p>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-300/70">Experiencia</p>
              <p className="text-3xl font-black text-white mt-2">+{formatReward(rewards.experience)}</p>
            </div>
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-amber-300/70">Dinero</p>
              <p className="text-3xl font-black text-white mt-2">+{formatReward(rewards.money)}</p>
            </div>
            <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-300/70">Daño hecho</p>
              <p className="text-3xl font-black text-white mt-2">{formatReward(victory.playerDamage)}</p>
            </div>
            <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-rose-300/70">Municion</p>
              <p className="text-3xl font-black text-white mt-2">{formatReward(victory.ammoRemaining)}</p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-700 bg-slate-950/70 p-5">
              <p className="text-xs uppercase tracking-[0.25em] text-slate-300/70">Desglose de recompensa</p>
              <div className="mt-3 space-y-2 text-sm text-slate-200">
                <p>XP base: <span className="text-white">+{formatReward(experience.base ?? rewards.experience)}</span></p>
                <p>Bonus de clan: <span className="text-white">+{formatReward(experience.clanBonus ?? rewards.experienceBonus)}</span></p>
                <p>XP total: <span className="text-white">+{formatReward(experience.total ?? rewards.experience)}</span></p>
                <div className="my-3 border-t border-slate-800" />
                <p>Dinero base: <span className="text-white">+{formatReward(money.base ?? rewards.moneyBase)}</span></p>
                <p>Bonus de clan: <span className="text-white">+{formatReward(money.clanBonus ?? rewards.moneyBonus)}</span></p>
                <p>Dinero total: <span className="text-white">+{formatReward(money.total ?? rewards.money)}</span></p>
                <div className="my-3 border-t border-slate-800" />
                <p>Puntos de nivel: <span className="text-white">+{formatReward(levelPoints.total ?? (rewards.levelPointsBonus || 0))}</span></p>
                <p>Bonus XP clan: <span className="text-white">{formatPercent(clanBonuses.clan?.xp || 0)}</span></p>
                <p>Bonus XP subclan: <span className="text-white">{formatPercent(clanBonuses.subclan?.xp || 0)}</span></p>
                <p>Bonus dinero clan: <span className="text-white">{formatPercent(clanBonuses.clan?.money || 0)}</span></p>
                <p>Bonus dinero subclan: <span className="text-white">{formatPercent(clanBonuses.subclan?.money || 0)}</span></p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-700 bg-slate-950/70 p-5">
              <p className="text-xs uppercase tracking-[0.25em] text-slate-300/70">Resumen</p>
              <div className="mt-3 space-y-2 text-sm text-slate-200">
                <p>Arma usada: <span className="text-white">{victory.weaponSlot || 'auto'}</span></p>
                <p>Objetivo: <span className="text-white">{victory.targetPart || '-'}</span></p>
                <p>Sector: <span className="text-white">#{victory.sectorId || victory.enemy?.id_sector || '-'}</span></p>
                <p>Bot: <span className="text-white">#{victory.bot?.id || '-'}</span></p>
                <p>Enemigo: <span className="text-white">#{victory.enemy?.id || '-'}</span></p>
                <p>Estado enemigo: <span className="text-white">{victory.enemyRemovedFromMap ? 'Eliminado del mapa' : 'Activo'}</span></p>
                <p>Registro de batalla: <span className="text-white">#{victory.battleReportId || '-'}</span></p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-700 bg-slate-950/70 p-5">
              <p className="text-xs uppercase tracking-[0.25em] text-slate-300/70">Ultimas acciones</p>
              <div className="mt-3 max-h-[240px] overflow-y-auto space-y-2 pr-1">
                {history.slice(-8).map((entry) => (
                  <div key={entry.id} className="rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <strong>{entry.title || entry.type}</strong>
                      <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{entry.actor || 'system'}</span>
                    </div>
                    <p className="mt-1 text-slate-300">{entry.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-300/70">Daño al enemigo</p>
              <div className="mt-3 space-y-1 text-sm text-cyan-100">
                {Object.keys(damageSummary.dealtToEnemy || {}).length ? Object.entries(damageSummary.dealtToEnemy).map(([part, value]) => (
                  <p key={part}>{part}: +{formatReward(value)}</p>
                )) : <p>No registrado</p>}
              </div>
            </div>
            <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-rose-300/70">Daño recibido</p>
              <div className="mt-3 space-y-1 text-sm text-rose-100">
                {Object.keys(damageSummary.receivedByBot || {}).length ? Object.entries(damageSummary.receivedByBot || {}).map(([part, value]) => (
                  <p key={part}>{part}: -{formatReward(value)}</p>
                )) : <p>No recibido</p>}
              </div>
            </div>
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-amber-300/70">Ultimo estado del enemigo</p>
              <div className="mt-3 space-y-1 text-sm text-amber-100">
                <p>Cabeza: {formatReward(finalEnemyState.v_head)}</p>
                <p>Torso: {formatReward(finalEnemyState.v_torso)}</p>
                <p>Brazo izq.: {formatReward(finalEnemyState.v_arm_l)}</p>
                <p>Brazo der.: {formatReward(finalEnemyState.v_arm_r)}</p>
                <p>Pierna izq.: {formatReward(finalEnemyState.v_leg_l)}</p>
                <p>Pierna der.: {formatReward(finalEnemyState.v_leg_r)}</p>
              </div>
            </div>
          </div>

          {loot?.granted && (
            <div className="rounded-2xl border border-fuchsia-500/20 bg-fuchsia-500/10 px-4 py-3 text-fuchsia-100 text-sm">
              Botin obtenido: {loot.item?.name || 'Objeto'} x{formatReward(loot.item?.quantity || 1)}
              {loot.storedInInventory ? ' guardado en inventario.' : ' soltado en el mapa.'}
            </div>
          )}

          {victory.levelUp && (
            <div className="rounded-2xl border border-violet-500/20 bg-violet-500/10 px-4 py-3 text-violet-100 text-sm">
              Ademas has subido de nivel. Pulsa el boton cuando quieras ver la pantalla de subida.
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {victory.levelUp ? (
              <button
                type="button"
                onClick={() => navigate('/level-up', { replace: true, state: { levelUp: victory.levelUp } })}
                className="rounded-xl border border-violet-500 bg-violet-600 px-5 py-3 text-white font-semibold hover:bg-violet-500"
              >
                Ver subida de nivel
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => navigate('/game', { replace: true })}
              className="rounded-xl border border-emerald-500 bg-emerald-600 px-5 py-3 text-white font-semibold hover:bg-emerald-500"
            >
              Volver al mapa
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
